import { useState, useRef, useEffect } from "react";
import type {
  ActiveElement,
  ChartData,
  ChartEvent,
  ChartOptions,
} from "chart.js";
import { Chart as ChartJS } from "chart.js";
import { Doughnut } from "react-chartjs-2";

export function DonutChart({
  segments,
  totalLabel,
  totalSublabel,
  externalHoverIdx,
  onHoverChange,
  size = 160,
  strokeWidth = 17,
  testId = "donut-chart",
}: {
  segments: { value: number; color: string; label: string }[];
  totalLabel: string;
  totalSublabel?: string;
  externalHoverIdx?: number | null;
  onHoverChange?: (idx: number | null) => void;
  size?: number;
  strokeWidth?: number;
  testId?: string;
}) {
  const [internalHoverIdx, setInternalHoverIdx] = useState<number | null>(null);
  const chartRef = useRef<ChartJS<"doughnut">>(null);

  const hoverIdx =
    externalHoverIdx !== undefined ? externalHoverIdx : internalHoverIdx;
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;

  const hovSeg = hoverIdx !== null ? segments[hoverIdx] : null;
  const segName = hovSeg ? hovSeg.label.split(" — ")[0] : null;
  const centerName =
    segName && segName.length > 14 ? segName.slice(0, 13) + "…" : segName;
  const centerLine1 = hovSeg
    ? `${Math.round((hovSeg.value / total) * 100)}%`
    : totalLabel;
  const centerLine2 = hovSeg ? (centerName ?? undefined) : totalSublabel;

  // Sync external hover → chart active elements
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    if (externalHoverIdx !== null && externalHoverIdx !== undefined) {
      chart.setActiveElements([{ datasetIndex: 0, index: externalHoverIdx }]);
    } else {
      chart.setActiveElements([]);
    }
    chart.update("none");
  }, [externalHoverIdx]);

  const cutoutPct = `${Math.max(0, (1 - (strokeWidth * 2) / size) * 100).toFixed(1)}%`;

  const chartData: ChartData<"doughnut"> = {
    datasets: [
      {
        data: segments.map((s) => s.value),
        backgroundColor: segments.map((s) => s.color),
        hoverOffset: 4,
        borderWidth: 0,
      },
    ],
  };

  const options: ChartOptions<"doughnut"> = {
    responsive: false,
    rotation: 0,
    cutout: cutoutPct,
    animation: { duration: 150 },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    onHover: (_event: ChartEvent, elements: ActiveElement[]) => {
      const idx = elements.length > 0 ? elements[0].index : null;
      setInternalHoverIdx(idx);
      onHoverChange?.(idx);
    },
  };

  return (
    <div
      style={{ position: "relative", width: size, height: size, flexShrink: 0 }}
      className="scd-chart-donut"
      data-testid={testId}
    >
      <Doughnut
        ref={chartRef}
        data={chartData}
        options={options}
        width={size}
        height={size}
      />
      {/* Center text overlay */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          pointerEvents: "none",
          width: "70%",
        }}
      >
        <div
          style={{
            fontSize: Math.round(size * 0.14),
            fontWeight: 700,
            fontFamily: "var(--font-onest, sans-serif)",
            color: "#3b82f6",
            lineHeight: 1,
          }}
        >
          {centerLine1}
        </div>
        {centerLine2 && (
          <div
            style={{
              fontSize: Math.round(size * 0.07),
              color: "#374151",
              fontFamily: "var(--font-onest, sans-serif)",
              marginTop: 4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {centerLine2}
          </div>
        )}
      </div>
    </div>
  );
}
